// Five illustrated Family Tree designs. `treeImage` is the realistic tree
// illustration used as the canvas backdrop; the rest are themed color tokens.

export const FAMILY_TREE_DESIGNS = {
  heritage_oak: {
    id: "heritage_oak",
    treeIcon: "/tree-icons/heritage_oak.png",
    name: "Heritage Oak",
    tagline: "Traditional • Warm • Timeless",
    description:
      "A full natural oak with warm parchment tones, soft gold connectors, and portrait cards nested into the canopy.",
    treeImage:
      "https://static.prod-images.emergentagent.com/jobs/51cbdc3e-df83-44f9-ad40-89e875f67af4/images/57c0ed632ed72fddedd35f5303e019c49aa7db24ac4eb649195ecea97b96e2c3.jpeg",
    background:
      "radial-gradient(circle at 50% 16%, rgba(255,255,255,.96), rgba(248,238,218,.82) 56%, rgba(235,218,186,.58) 100%)",
    overlay: "linear-gradient(180deg, rgba(255,251,242,.35), rgba(255,248,234,.55))",
    trunk: "#6A4728",
    branch: "#7B5732",
    foliage: ["#6C7D3F", "#7D8F4F", "#526B36", "#8EA05B"],
    cardBackground: "#FFFDF8",
    cardBorder: "#B9873B",
    text: "#2B241B",
    mutedText: "#6D6254",
    relationship: { married: "#C69B3C", partnered: "#8E63C7", child: "#4D9BE6", sibling: "#6EA35A" },
    portraitRing: "#D6AE58",
    controlBackground: "#FFF7E8",
    controlText: "#5E431F",
    accent: "#B57A22",
    shadow: "0 10px 28px rgba(91, 65, 34, .18)",
  },

  golden_legacy: {
    id: "golden_legacy",
    treeIcon: "/tree-icons/golden_legacy.png",
    name: "Golden Legacy",
    tagline: "Elegant • Luxury • Royal",
    description:
      "A deep navy family tree with metallic gold foliage, illuminated portrait frames, and premium black-and-gold controls.",
    treeImage:
      "https://static.prod-images.emergentagent.com/jobs/51cbdc3e-df83-44f9-ad40-89e875f67af4/images/dcaa01c0b533e0aaa611e3e95df218f88891fbdcff86264d32e1237be6b7cf1c.jpeg",
    background:
      "radial-gradient(circle at 50% 12%, rgba(17,49,83,.98), #071B33 58%, #041223 100%)",
    overlay: "radial-gradient(circle at 50% 32%, rgba(212,175,90,.12), transparent 62%)",
    trunk: "#9A6C20",
    branch: "#D4AF5A",
    foliage: ["#D4AF5A", "#C9942D", "#F0CF78", "#A97A23"],
    cardBackground: "#0D2748",
    cardBorder: "#D4AF5A",
    text: "#FFF8E4",
    mutedText: "#E3C978",
    relationship: { married: "#F2CA62", partnered: "#B486EA", child: "#5DB1FF", sibling: "#80C46B" },
    portraitRing: "#F0C75E",
    controlBackground: "#102B4E",
    controlText: "#FBE7A5",
    accent: "#D4AF5A",
    shadow: "0 12px 34px rgba(0, 0, 0, .40)",
    dark: true,
  },

  modern_minimal: {
    id: "modern_minimal",
    treeIcon: "/tree-icons/modern_minimal.png",
    name: "Modern Minimal",
    tagline: "Clean • Simple • Contemporary",
    description:
      "A light sculptural tree with airy branches, restrained leaf details, neutral portrait cards, and minimal controls.",
    treeImage:
      "https://static.prod-images.emergentagent.com/jobs/51cbdc3e-df83-44f9-ad40-89e875f67af4/images/486a0d96b04fd7c702ea69f0a71541534fadeba560f6abaea5393fb6fa9f611d.jpeg",
    background: "linear-gradient(180deg, #FFFFFF 0%, #F5F7F8 62%, #EDF1F2 100%)",
    overlay: "linear-gradient(180deg, rgba(255,255,255,.12), rgba(244,246,247,.08))",
    trunk: "#A7ADA9",
    branch: "#B8C0BA",
    foliage: ["#B6C4B4", "#D1D8CF", "#AAB8A7", "#DDE3DA"],
    cardBackground: "#FFFFFF",
    cardBorder: "#D4D9DB",
    text: "#1F2A2E",
    mutedText: "#6F787B",
    relationship: { married: "#A5A9A8", partnered: "#9A83B8", child: "#7AA7C7", sibling: "#8DA483" },
    portraitRing: "#C8CECB",
    controlBackground: "#FFFFFF",
    controlText: "#344047",
    accent: "#71816D",
    shadow: "0 9px 24px rgba(43, 53, 57, .10)",
  },

  cherry_blossom: {
    id: "cherry_blossom",
    treeIcon: "/tree-icons/cherry_blossom.png",
    name: "Cherry Blossom",
    tagline: "Soft • Elegant • Graceful",
    description:
      "A flowering pink tree with blush portrait frames, petal accents, soft ivory cards, and graceful rose-toned controls.",
    treeImage:
      "https://static.prod-images.emergentagent.com/jobs/51cbdc3e-df83-44f9-ad40-89e875f67af4/images/f04f14ca90f60c98fe885edd0413f9a9be8be0c88c33396863af79378796b6f1.jpeg",
    background: "radial-gradient(circle at 50% 14%, #FFFDFE, #FFF2F6 58%, #FBE3EB 100%)",
    overlay: "radial-gradient(circle at 50% 30%, rgba(255,255,255,.45), transparent 70%)",
    trunk: "#7A4E45",
    branch: "#9A655B",
    foliage: ["#F4A9BC", "#F7C0CF", "#E789A5", "#F8D6DE"],
    cardBackground: "#FFFDFE",
    cardBorder: "#EAA0B4",
    text: "#5B2A39",
    mutedText: "#956273",
    relationship: { married: "#CE9D55", partnered: "#A86DD0", child: "#67A9DD", sibling: "#81A96E" },
    portraitRing: "#E990AA",
    controlBackground: "#FFF7FA",
    controlText: "#8A3F59",
    accent: "#D66588",
    shadow: "0 10px 28px rgba(154, 83, 105, .16)",
  },

  evergreen: {
    id: "evergreen",
    treeIcon: "/tree-icons/evergreen.png",
    name: "Evergreen",
    tagline: "Strong • Natural • Enduring",
    description:
      "A tall evergreen design with layered forest branches, mountain atmosphere, cream portrait cards, and deep green controls.",
    treeImage:
      "https://static.prod-images.emergentagent.com/jobs/51cbdc3e-df83-44f9-ad40-89e875f67af4/images/cb5da6ac5df5299afc0c2ed5bdafe718f9ef62b1b1203c0bc0e1a54b66c088a7.jpeg",
    background: "linear-gradient(180deg, #EEF2EE 0%, #DFE8E1 48%, #C7D6CA 100%)",
    overlay: "linear-gradient(180deg, rgba(255,255,255,.30), rgba(31,72,54,.10))",
    trunk: "#4A3628",
    branch: "#315B43",
    foliage: ["#214C38", "#2C6348", "#3A7655", "#173F2E"],
    cardBackground: "#FFFDF8",
    cardBorder: "#6E8A72",
    text: "#18372A",
    mutedText: "#607267",
    relationship: { married: "#C8A04A", partnered: "#8B6DB7", child: "#4F98D1", sibling: "#71A15E" },
    portraitRing: "#607D62",
    controlBackground: "#F7FBF8",
    controlText: "#284C39",
    accent: "#315F45",
    shadow: "0 12px 30px rgba(28, 68, 49, .18)",
  },
};

export const DESIGN_ORDER = [
  "heritage_oak",
  "golden_legacy",
  "modern_minimal",
  "cherry_blossom",
  "evergreen",
];

export function getDesign(id) {
  return FAMILY_TREE_DESIGNS[id] || FAMILY_TREE_DESIGNS.heritage_oak;
}
